<template>
  <div class="details-panel">
    <template v-if="eventsLoading">
      <p class="eyebrow">Searching</p>
      <div class="skeleton title-skeleton"></div>
      <div class="skeleton line-skeleton"></div>
      <div class="skeleton line-skeleton short"></div>
    </template>

    <template v-else-if="activeEvent.id">
      <div class="details-heading">
        <span class="event-kind">{{ eventKind }}</span>
        <h3>{{ activeEvent.title || 'Ceremony moment' }}</h3>
        <p v-if="eventDescription">{{ eventDescription }}</p>
      </div>

      <dl class="timing">
        <div>
          <dt>Starts at</dt>
          <dd>{{ formatDuration(activeEvent.timestamp) }}</dd>
        </div>
        <div>
          <dt>Duration</dt>
          <dd>{{ activeEvent.duration }}</dd>
        </div>
      </dl>

      <div class="highlight-action">
        <button
          v-if="!isAddedToHighlights"
          type="button"
          :disabled="collectionSaving"
          @click="$emit('add-to-collection', activeEvent)"
        >
          {{ collectionActionLabel }}
        </button>
        <div v-else class="added-status" role="status">
          <span class="added-check" aria-hidden="true">✓</span>
          <span class="added-copy">
            <small>Saved to</small>
            <strong>{{ collectionName || 'collection' }}</strong>
          </span>
          <button
            type="button"
            class="remove-highlight"
            :disabled="collectionSaving"
            @click="$emit('remove-from-collection', collectionItem)"
          >
            Remove
          </button>
        </div>
        <div v-if="!isAddedToHighlights" class="collection-target">
          <span v-if="collectionName">Saving to {{ collectionName }}</span>
          <span v-else>Choose or create a collection</span>
          <button type="button" :disabled="collectionSaving" @click="$emit('choose-collection')">
            {{ collectionName ? 'Change' : 'Choose' }}
          </button>
        </div>
        <p v-if="collectionError" role="alert">{{ collectionError }}</p>
      </div>

      <div class="export-wrap">
        <ceremony-section-export
          :active-event="activeEvent"
          :ceremony-id="ceremonyId"
          :export-states="exportStates"
          :format-duration="formatDuration"
          :active-video-url="activeVideoUrl"
          :video-options="videoOptions"
          :ceremony-video-duration="ceremonyVideoDuration"
          :cut-times="cutTimes"
          @modal-open="$emit('export-modal-open')"
          @export-event="$emit('export-event', $event)"
          @download-export="$emit('download-export', $event)"
        />
      </div>

      <section v-if="isSearchResult" class="feedback">
        <p class="feedback-title">Did this match your search?</p>
        <template v-if="!eventFeedback">
          <div class="feedback-actions">
            <button
              type="button"
              :disabled="feedbackSubmitting"
              @click="submitFeedback('yes')"
            >
              Yes
            </button>
            <button
              type="button"
              class="secondary"
              :disabled="feedbackSubmitting"
              @click="submitFeedback('no')"
            >
              No
            </button>
          </div>
          <p v-if="feedbackError" class="feedback-error" role="alert">
            {{ feedbackError }}
          </p>
        </template>
        <p v-else class="feedback-saved">
          Feedback recorded: {{ recordedFeedbackResponse }}
        </p>
      </section>
    </template>

    <div v-else class="empty-details">
      <span aria-hidden="true">↖</span>
      <h3>Choose a moment</h3>
      <p>Select a chapter, event or suggested moment to see its details.</p>
    </div>
  </div>
</template>

<script>
import CeremonySectionExport from './CeremonySectionExport.vue'

export default {
  name: 'CeremonyEventDetails',
  components: {
    CeremonySectionExport,
  },
  props: {
    activeEvent: {
      type: Object,
      default: () => ({}),
    },
    eventsLoading: {
      type: Boolean,
      default: false,
    },
    formatDuration: {
      type: Function,
      required: true,
    },
    eventFeedback: {
      type: [String, Object],
      default: '',
    },
    ceremonyId: {
      type: String,
      default: '',
    },
    exportStates: {
      type: Object,
      default: () => ({}),
    },
    activeVideoUrl: {
      type: String,
      default: '',
    },
    videoOptions: {
      type: Object,
      required: true,
    },
    ceremonyVideoDuration: {
      type: Number,
      default: 0,
    },
    cutTimes: {
      type: Array,
      default: () => [],
    },
    feedbackSubmitting: {
      type: Boolean,
      default: false,
    },
    feedbackError: {
      type: String,
      default: '',
    },
    collectionItem: {
      type: Object,
      default: null,
    },
    collectionSaving: {
      type: Boolean,
      default: false,
    },
    collectionError: {
      type: String,
      default: '',
    },
    collectionName: {
      type: String,
      default: '',
    },
  },
  computed: {
    isAddedToHighlights() {
      return Boolean(this.collectionItem?.item_id)
    },
    collectionActionLabel() {
      if (this.collectionSaving) return 'Saving…'
      return 'Save to collection'
    },
    isSearchResult() {
      return ['frame', 'transcript'].includes(this.activeEvent?.type)
    },
    eventKind() {
      if (this.activeEvent?.kind === 'standout') return 'Standout moment'
      if (this.activeEvent?.candidateType === 'chapter') return 'Chapter search result'
      if (this.activeEvent?.candidateType === 'event') return 'Ceremony event search result'
      const labels = {
        highlight: 'Ceremony moment',
        chapter: 'Chapter',
        chapter_event: 'Ceremony event',
        frame: 'Visual search result',
        transcript: 'Spoken match',
      }
      return labels[this.activeEvent?.type] || 'Ceremony moment'
    },
    eventDescription() {
      const description = String(
        this.activeEvent?.description || this.activeEvent?.headline || ''
      ).trim()
      return description === this.activeEvent?.title ? '' : description
    },
    recordedFeedbackResponse() {
      if (typeof this.eventFeedback === 'string') return this.eventFeedback
      return this.eventFeedback?.response || ''
    },
  },
  methods: {
    submitFeedback(response) {
      if (!this.activeEvent?.id || this.eventFeedback) return
      this.$emit('submit-feedback', {
        eventId: this.activeEvent.id,
        response,
      })
    },
  },
}
</script>

<style scoped>
.details-panel {
  min-height: 420px;
  padding: 20px;
  border: 1px solid #e4e8ef;
  border-radius: 16px;
  background: #fff;
}

.details-heading {
  padding-bottom: 18px;
  border-bottom: 1px solid #edf0f4;
}

.event-kind,
.eyebrow {
  color: #7c3aed;
  font-size: 0.68rem;
  font-weight: 750;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.details-heading h3,
.empty-details h3 {
  overflow-wrap: anywhere;
  margin: 8px 0 7px;
  color: #172033;
  font-size: 1.05rem;
  font-weight: 720;
  line-height: 1.35;
}

.details-heading p,
.empty-details p {
  margin: 0;
  color: #667085;
  font-size: 0.78rem;
  line-height: 1.55;
}

.timing {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin: 18px 0;
}

.timing div {
  border-radius: 10px;
  padding: 11px;
  background: #f8f9fb;
}

.timing dt {
  margin-bottom: 4px;
  color: #7c8494;
  font-size: 0.66rem;
}

.timing dd {
  margin: 0;
  color: #1d2939;
  font-size: 0.9rem;
  font-weight: 720;
  font-variant-numeric: tabular-nums;
}

.export-wrap {
  padding-top: 4px;
}

.highlight-action {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.highlight-action > button:first-child {
  min-height: 44px;
  border: 1px solid #6d28d9;
  border-radius: 10px;
  background: #6d28d9;
  color: #fff;
  font-size: 0.78rem;
  font-weight: 720;
  cursor: pointer;
}

.highlight-action > button:first-child:hover:not(:disabled) {
  background: #5b21b6;
}

.added-status {
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: 24px minmax(0, 1fr) auto;
  min-height: 56px;
  align-items: center;
  gap: 10px;
  padding: 9px 11px;
  border: 1px solid #d8d0f4;
  border-radius: 10px;
  background: #f8f6ff;
}

.added-check {
  display: inline-flex;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #ede9fe;
  color: #6d28d9;
  font-size: 0.75rem;
  font-weight: 800;
}

.added-copy {
  display: grid;
  min-width: 0;
  gap: 2px;
}

.added-copy small {
  color: #777f90;
  font-size: 0.64rem;
  line-height: 1.2;
}

.added-copy strong {
  overflow-wrap: anywhere;
  color: #4c1d95;
  font-size: 0.72rem;
  font-weight: 720;
  line-height: 1.3;
}

.highlight-action button:focus-visible {
  outline: 3px solid rgba(109, 40, 217, 0.25);
  outline-offset: 2px;
}

.highlight-action button:disabled {
  cursor: default;
}

.remove-highlight {
  min-height: 36px;
  padding: 0 2px 0 8px;
  border: 0;
  background: transparent;
  color: #667085;
  font-size: 0.74rem;
  font-weight: 650;
  cursor: pointer;
}

.highlight-action p {
  grid-column: 1 / -1;
  margin: 0;
  color: #b42318;
  font-size: 0.72rem;
}

.collection-target {
  display: flex;
  grid-column: 1 / -1;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  color: #747b8b;
  font-size: 0.69rem;
}

.collection-target button {
  padding: 3px 0;
  border: 0;
  background: transparent;
  color: #6d28d9;
  font: inherit;
  font-weight: 720;
  cursor: pointer;
}

.feedback {
  margin-top: 20px;
  padding-top: 18px;
  border-top: 1px solid #edf0f4;
}

.feedback-title {
  margin: 0 0 10px;
  color: #344054;
  font-size: 0.78rem;
  font-weight: 700;
}

.feedback-actions {
  display: flex;
  gap: 8px;
}

.feedback-actions button {
  min-width: 70px;
  border: 1px solid #17132f;
  border-radius: 8px;
  padding: 7px 12px;
  background: #17132f;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
}

.feedback-actions button.secondary {
  background: #fff;
  color: #17132f;
}

.feedback-actions button:disabled {
  opacity: 0.45;
}

.feedback-error,
.feedback-saved {
  margin: 9px 0 0;
  font-size: 0.72rem;
}

.feedback-error {
  color: #b42318;
}

.feedback-saved {
  color: #067647;
}

.empty-details {
  display: grid;
  min-height: 350px;
  align-content: center;
  justify-items: center;
  padding: 20px;
  text-align: center;
}

.empty-details > span {
  color: #9e77ed;
  font-size: 1.5rem;
}

.skeleton {
  border-radius: 8px;
  background: #edf0f4;
}

.title-skeleton {
  width: 72%;
  height: 24px;
  margin: 16px 0;
}

.line-skeleton {
  width: 100%;
  height: 11px;
  margin-bottom: 9px;
}

.line-skeleton.short {
  width: 58%;
}

@media (max-width: 899px) {
  .details-panel {
    min-height: 0;
  }
}
</style>
